import type { ReactElement } from 'react';

type SlideComponent = () => ReactElement;

// Slide files live directly in the presentation folder and use the "NN-slidename.tsx" format
const SLIDE_FILE_PATTERN = /^(\d+)-[^/]+\.tsx$/;

const hasSlideExport = (module: unknown): module is { slide: SlideComponent } =>
  typeof module === 'object' &&
  module !== null &&
  'slide' in module &&
  typeof module.slide === 'function';

/**
 * Selects and sorts the slide components of one presentation folder
 * @param modules - Modules keyed by absolute path, as returned by import.meta.glob
 * @param folder - The presentation folder name to collect slides from
 * @returns Array of slide component functions, sorted by slide number
 */
export const collectSlides = (
  modules: Record<string, unknown>,
  folder: string
): SlideComponent[] => {
  const folderPrefix = `/presentations/${folder}/`;
  const slides: Array<{ slide: SlideComponent; number: number }> = [];

  for (const [path, module] of Object.entries(modules)) {
    if (!path.startsWith(folderPrefix)) {
      continue;
    }

    const match = SLIDE_FILE_PATTERN.exec(path.slice(folderPrefix.length));
    if (!match) {
      continue;
    }

    if (!hasSlideExport(module)) {
      console.warn(`Skipping ${path}: it does not export a slide component`);
      continue;
    }

    slides.push({ slide: module.slide, number: parseInt(match[1], 10) });
  }

  // Ensure at least one slide was found
  if (slides.length === 0) {
    throw new Error(`Could not find any slides in folder ${folder}`);
  }

  // Sort slides by number and return array of slide components only
  return slides.sort((a, b) => a.number - b.number).map(({ slide }) => slide);
};
